/**
 * Support for the T-Lián desktop app (Electron, folder desktop/). In a normal browser `desktop` is null
 * and nothing here does anything. In the app, new messages become native notifications and the unread
 * count goes on the Dock / taskbar. Like push, notifications never contain message text.
 */
import type { ConversationDTO, MessageDTO, UserSummaryDTO } from '@tc/contracts';

interface DesktopBridge {
  platform: string;
  setBadge(count: number, overlay?: string | null): void;
  notify(n: { title: string; body: string; url: string; mention: boolean }): void;
  onOpenUrl(cb: (url: string) => void): void;
  /** Apps from 0.1.2 on download new versions themselves and only ask to restart. */
  canSelfUpdate?: boolean;
  onUpdate?(cb: (u: { state: 'ready'; version: string }) => void): void;
  installUpdate?(): void;
}

export const desktop: DesktopBridge | null =
  typeof window !== 'undefined' ? ((window as unknown as { tlianDesktop?: DesktopBridge }).tlianDesktop ?? null) : null;

const NOTIFY_KEY = 'tc.desktopNotify';
export function desktopNotifyEnabled(): boolean {
  try {
    return localStorage.getItem(NOTIFY_KEY) !== 'off';
  } catch {
    return true;
  }
}
export function setDesktopNotifyEnabled(on: boolean): void {
  try {
    if (on) localStorage.removeItem(NOTIFY_KEY);
    else localStorage.setItem(NOTIFY_KEY, 'off');
  } catch {
    /* ignore */
  }
}

const TEXT = {
  'sv-SE': { message: 'Nytt meddelande', mention: 'Nämnde dig', file: 'Skickade en bild eller fil', someone: 'Någon' },
  'zh-Hans': { message: '新消息', mention: '提到了你', file: '发送了图片或文件', someone: '有人' },
  en: { message: 'New message', mention: 'Mentioned you', file: 'Sent an image or file', someone: 'Someone' },
} as const;
const lang = (l: string | undefined): keyof typeof TEXT => (l === 'sv-SE' ? 'sv-SE' : l?.startsWith('zh') ? 'zh-Hans' : 'en');

/** A native notification for a new message (called only when the person is not looking at that chat). */
export function notifyDesktop(m: MessageDTO, conv: ConversationDTO | undefined, sender: UserSummaryDTO | undefined, me: { id: string; uiLocale: string } | null): void {
  if (!desktop || !desktopNotifyEnabled()) return;
  const tx = TEXT[lang(me?.uiLocale)];
  const name = sender?.displayName ?? tx.someone;
  const mention = !!me && (m.mentions ?? []).includes(me.id);
  desktop.notify({
    title: conv?.type === 'group' && conv.title ? `${name} · ${conv.title}` : name,
    body: mention ? tx.mention : m.attachment ? tx.file : tx.message,
    url: `/?c=${m.conversationId}`,
    mention,
  });
}

/** Small red badge with the count, used as the Windows taskbar overlay. */
export function badgeOverlay(count: number): string | null {
  if (count <= 0 || typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = c.height = 32;
  const g = c.getContext('2d');
  if (!g) return null;
  const label = count > 99 ? '99+' : String(count);
  g.fillStyle = '#e5322d';
  g.beginPath();
  g.arc(16, 16, 16, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = '#ffffff';
  g.font = `bold ${label.length > 2 ? 12 : label.length > 1 ? 16 : 20}px system-ui, sans-serif`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(label, 16, 17);
  return c.toDataURL('image/png');
}
